import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { ArrowDown, CircleAlert, CirclePlus, ChevronsUpDown, Check, X } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export default function ReminderButton() {
  const [open, setOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [type, setType] = useState(null);
  const [note, setNote] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !suppliers.length) {
      api.get('/suppliers').then(({ data }) => setSuppliers(data)).catch(() => {});
    }
  }, [open, suppliers.length]);

  const reset = () => { setSupplier(null); setType(null); setNote(''); setPriority('NORMAL'); };

  const save = async () => {
    if (!supplier) { toast.error('Pick a supplier'); return; }
    if (!type) { toast.error('Pick a reminder type'); return; }
    if (!note.trim()) { toast.error('Write a short note'); return; }
    setSaving(true);
    try {
      await api.post('/reminders', { supplier, reminder_type: type, note: note.trim(), priority });
      toast.success(`Reminder saved for ${supplier}`);
      window.dispatchEvent(new CustomEvent('og-reminder-created', { detail: { supplier } }));
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <button
        data-testid="reminder-fab"
        onClick={() => setOpen(true)}
        title="Add a supplier reminder"
        style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999 }}
        className="h-12 w-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/30 flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
      >
        <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-20" />
        <ArrowDown className="h-5 w-5 relative" strokeWidth={2.5} />
      </button>

      {open && (
        <>
          <div
            data-testid="reminder-overlay"
            onClick={() => { if (!saving) { setOpen(false); reset(); } }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9998 }}
          />
          <div
            data-testid="reminder-dialog"
            role="dialog"
            style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000 }}
            className="w-[calc(100vw-2rem)] max-w-md bg-white border border-zinc-200 shadow-xl p-6"
          >
            <button
              data-testid="reminder-dialog-close"
              onClick={() => { if (!saving) { setOpen(false); reset(); } }}
              className="absolute right-4 top-4 text-zinc-400 hover:text-zinc-700"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: "'Chivo', sans-serif" }}>Quick Supplier Reminder</h2>
              <p className="text-sm text-zinc-500 mt-1">It'll pop up automatically next time you open this supplier.</p>
            </div>
            <div className="space-y-3">
              <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
                <PopoverTrigger asChild>
                  <Button data-testid="reminder-supplier-select" variant="outline" role="combobox"
                    className="w-full h-10 rounded-none border-zinc-300 justify-between font-normal">
                    <span className={cn('truncate', !supplier && 'text-zinc-400')}>{supplier || 'Select supplier...'}</span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0 rounded-none z-[10001]" align="start">
                  <Command>
                    <CommandInput placeholder="Type supplier name..." className="h-9 text-sm" />
                    <CommandList>
                      <CommandEmpty className="py-3 text-xs text-zinc-400 text-center">No supplier found.</CommandEmpty>
                      <CommandGroup>
                        {suppliers.map((s) => (
                          <CommandItem key={s} value={s} onSelect={() => { setSupplier(s); setSupplierOpen(false); }}
                            className="rounded-none text-sm cursor-pointer">
                            <Check className={cn('mr-2 h-3.5 w-3.5', supplier === s ? 'opacity-100' : 'opacity-0')} />
                            {s}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <div className="grid grid-cols-2 gap-2">
                <button data-testid="reminder-type-oos" onClick={() => setType('OUT_OF_STOCK')}
                  className={`flex items-center gap-2 border p-2.5 text-xs font-semibold transition-colors ${
                    type === 'OUT_OF_STOCK' ? 'border-red-400 bg-red-50 text-red-700' : 'border-zinc-200 text-zinc-500 hover:border-red-300'
                  }`}>
                  <CircleAlert className="h-4 w-4 text-red-500 shrink-0" /> Out of Stock / Very Low
                </button>
                <button data-testid="reminder-type-enquiry" onClick={() => setType('NEW_ENQUIRY')}
                  className={`flex items-center gap-2 border p-2.5 text-xs font-semibold transition-colors ${
                    type === 'NEW_ENQUIRY' ? 'border-sky-400 bg-sky-50 text-sky-700' : 'border-zinc-200 text-zinc-500 hover:border-sky-300'
                  }`}>
                  <CirclePlus className="h-4 w-4 text-sky-500 shrink-0" /> New Product Enquiry
                </button>
              </div>

              <Textarea
                data-testid="reminder-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Surf Excel 1kg almost empty — ask for 2 cases"
                rows={3}
                className="rounded-none border-zinc-300 text-sm focus:border-amber-500 focus-visible:ring-0"
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button data-testid="reminder-priority-normal" onClick={() => setPriority('NORMAL')}
                    className={`px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                      priority === 'NORMAL' ? 'border-zinc-400 bg-zinc-100 text-zinc-700' : 'border-zinc-200 text-zinc-400'
                    }`}>Normal</button>
                  <button data-testid="reminder-priority-important" onClick={() => setPriority('IMPORTANT')}
                    className={`px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                      priority === 'IMPORTANT' ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-zinc-200 text-zinc-400'
                    }`}>Important</button>
                </div>
                <Button data-testid="reminder-save-btn" onClick={save} disabled={saving}
                  className="rounded-none bg-amber-500 hover:bg-amber-600 h-9">
                  {saving ? 'Saving...' : 'Add Reminder'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>,
    document.body
  );
}
